/* eslint-disable no-unused-vars */
// JobOfferLetterTemplate_Production.js
// Fully functional and productive job offer letter template system
// Production-ready with comprehensive features, PDF generation, and business value

import React, { useState, useEffect, useCallback } from 'react';
import {
  View,
  Text,
  StyleSheet,
  TouchableOpacity,
  ScrollView,
  TextInput,
  Alert,
  Modal,
  Dimensions,
  Platform,
  Share,
  ActivityIndicator,
  Switch,
  KeyboardAvoidingView,
  StatusBar,
} from 'react-native';
import {
  MaterialCommunityIcons,
  Ionicons,
  AntDesign,
  FontAwesome5,
  MaterialIcons,
} from '@expo/vector-icons';
import { LinearGradient } from 'expo-linear-gradient';
import AsyncStorage from '@react-native-async-storage/async-storage';
import * as FileSystem from 'expo-file-system';
import * as Sharing from 'expo-sharing';

// ===== API Configuration =====
const API_BASE_URL = 'https://api.jobeng.ai';

const getCompanyId = async () => {
  try {
    const userData = await AsyncStorage.getItem('currentUser');
    if (userData) {
      const user = JSON.parse(userData);
      return user.company_id || user.companyId || null;
    }
    const companyProfile = await AsyncStorage.getItem('companyProfile');
    if (companyProfile) {
      const company = JSON.parse(companyProfile);
      return company.id || company.company_id || null;
    }
    return null;
  } catch (e) {
    console.error('Error getting company_id:', e);
    return null;
  }
};

const apiRequest = async (endpoint, options = {}) => {
  try {
    const companyId = await getCompanyId();
    // Split endpoint and query string
    const [path, queryString] = endpoint.split('?');
    // Route: auth/* goes to /api/auth/*, everything else to /api/data/{table}
    let baseUrl;
    if (path.startsWith('auth/')) {
      baseUrl = `${API_BASE_URL}/api/${path}`;
    } else {
      // Convert hyphens to underscores for DB table mapping
      const dbPath = path.replace(/-/g, '_');
      baseUrl = `${API_BASE_URL}/api/data/${dbPath}`;
    }
    const params = new URLSearchParams(queryString || '');
    if (companyId) params.set('company_id', companyId);
    const separator = params.toString() ? '?' : '';
    const url = `${baseUrl}${separator}${params.toString()}`;

    const response = await fetch(url, {
      headers: { 'Content-Type': 'application/json', 'X-API-Key': '16a0651c78da416445e9c2946fb72fd9a6e4ce4a71ba610db15251050c3462ef' },
      ...options,
      ...(options.body ? { body: JSON.stringify(options.body) } : {}),
    });

    if (!response.ok) throw new Error(`API error: ${response.status}`);
    const data = await response.json();
    return data;
  } catch (error) {
    console.error(`API request failed for ${endpoint}:`, error);
    throw error;
  }
};
// ===== End API Configuration =====


const { width, height } = Dimensions.get('window');

const JobOfferLetterTemplate_Production = ({ 
  navigation, 
  route, 
  onClose, 
  onDownload, 
  onSave 
}) => {
  // Safely extract route params with comprehensive fallbacks
  const offer = route?.params?.offer || {
    id: 'default_offer',
    candidateName: 'John Smith',
    candidateEmail: 'john.smith@example.com',
    candidateAddress: '123 Main Street, London, UK',
    position: 'Senior Software Engineer',
    department: 'Engineering',
    salary: '£75,000',
    startDate: '2025-09-01',
    deadline: '2025-08-25',
    location: 'London, UK',
    workType: 'Hybrid',
    reportingTo: 'Engineering Manager',
    probationPeriod: '6 months',
    noticePeriod: '1 month',
    workingHours: '37.5 hours per week',
    benefits: [
      'Health, dental, and vision insurance',
      'Pension scheme with company contribution',
      '25 days paid annual leave',
      'Flexible working arrangements',
      'Professional development budget',
      'Life insurance coverage',
      'Employee assistance program',
      'Cycle to work scheme'
    ],
  };

  const companyInfo = route?.params?.companyInfo || {
    name: 'Jobeng Technologies Ltd',
    registrationNumber: 'Company Registration: 12345678',
    address: '123 Business Street, London, EC1A 1BB, United Kingdom',
    phone: '+44 20 7123 4567',
    email: 'hr@jobeng.tech',
    website: 'www.jobeng.tech',
    hrDirector: 'Sarah Thompson',
    hrDirectorTitle: 'Director of Human Resources',
    hrDirectorEmail: 'sarah.thompson@jobeng.tech',
    hrDirectorPhone: '+44 20 7123 4568',
    ceo: 'Michael Johnson',
    ceoTitle: 'Chief Executive Officer',
  };

  // State management
  const [letterData, setLetterData] = useState({
    candidateName: offer.candidateName,
    candidateEmail: offer.candidateEmail,
    candidateAddress: offer.candidateAddress,
    position: offer.position,
    department: offer.department,
    salary: offer.salary,
    startDate: offer.startDate,
    deadline: offer.deadline,
    location: offer.location,
    workType: offer.workType,
    reportingTo: offer.reportingTo,
    probationPeriod: offer.probationPeriod,
    noticePeriod: offer.noticePeriod,
    workingHours: offer.workingHours,
    benefits: offer.benefits,
    customClauses: '',
    confidentialityClause: true,
    nonCompeteClause: false,
    backgroundCheckClause: true,
    rightToWorkClause: true,
    dataProtectionClause: true,
  });

  const [letterSettings, setLetterSettings] = useState({
    letterType: 'standard',
    includeCompanyLogo: true,
    includeWatermark: false,
    pageNumbers: true,
    digitalSignature: false,
    legalReview: false,
    urgentOffer: false,
  });

  const [isLoading, setIsLoading] = useState(false);
  const [isGenerating, setIsGenerating] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [showSettings, setShowSettings] = useState(false);
  const [showTemplateSelector, setShowTemplateSelector] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState('standard');
  const [customTemplates, setCustomTemplates] = useState([]);
  const [letterHistory, setLetterHistory] = useState([]);

  // Load data on component mount
  useEffect(() => {
    loadLetterHistory();
    loadCustomTemplates();
  }, []);

  // Load letter history
  const loadLetterHistory = async () => {
    try {
      // Try API first
      let historyLoaded = false;
      try {
        const resp = await apiRequest('offer-letter-history');
        if (resp.success && resp.data) {
          setLetterHistory(resp.data);
          await AsyncStorage.setItem('jobOfferLetterHistory', JSON.stringify(resp.data));
          historyLoaded = true;
        }
      } catch (apiErr) {
        const history = await AsyncStorage.getItem('jobOfferLetterHistory');
        if (history) {
          setLetterHistory(JSON.parse(history));
          historyLoaded = true;
        }
      }
    } catch (error) {
      console.error('Error loading letter history:', error);
    }
  };

  // Load custom templates
  const loadCustomTemplates = async () => {
    try {
      // Try API first for templates
      try {
        const resp = await apiRequest('offer-letter-templates');
        if (resp.success && resp.data) {
          setCustomTemplates(resp.data);
          await AsyncStorage.setItem('customOfferLetterTemplates', JSON.stringify(resp.data));
        }
      } catch (apiErr) {
        const templates = await AsyncStorage.getItem('customOfferLetterTemplates');
        if (templates) {
          setCustomTemplates(JSON.parse(templates));
        }
      }
    } catch (error) {
      console.error('Error loading custom templates:', error);
    }
  };

  // Save letter to history
  const saveToHistory = async (letterData) => {
    try {
      const newHistoryItem = {
        id: Date.now().toString(),
        candidateName: letterData.candidateName,
        position: letterData.position,
        generatedAt: new Date().toISOString(),
        status: 'generated',
        template: selectedTemplate,
        ...letterData,
      };

      const updatedHistory = [newHistoryItem, ...letterHistory.slice(0, 49)]; // Keep last 50
      setLetterHistory(updatedHistory);
      try { await apiRequest('offer-letter-history', { method: 'POST', body: { history: updatedHistory } }); } catch(e) { console.warn('API save failed:', e.message); }
      await AsyncStorage.setItem('jobOfferLetterHistory', JSON.stringify(updatedHistory));
    } catch (error) {
      console.error('Error saving to history:', error);
    }
  };

  // Handle input changes
  const handleInputChange = (field, value) => {
    setLetterData(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle letter settings changes
  const handleLetterSettingsChange = (field, value) => {
    setLetterSettings(prev => ({
      ...prev,
      [field]: value,
    }));
  };

  // Handle benefits array changes
  const handleBenefitChange = (index, value) => {
    const newBenefits = [...letterData.benefits];
    newBenefits[index] = value;
    setLetterData(prev => ({
      ...prev,
      benefits: newBenefits,
    }));
  };

  // Add new benefit
  const addBenefit = () => {
    setLetterData(prev => ({
      ...prev,
      benefits: [...prev.benefits, ''],
    }));
  };

  // Remove benefit
  const removeBenefit = (index) => {
    const newBenefits = letterData.benefits.filter((_, i) => i !== index);
    setLetterData(prev => ({
      ...prev,
      benefits: newBenefits,
    }));
  };

  // Generate letter content based on selected template
  const generateLetterContent = () => {
    const templates = {
      standard: generateStandardLetter(),
      executive: generateExecutiveLetter(),
      contract: generateContractLetter(),
      intern: generateInternLetter(),
      consultant: generateConsultantLetter(),
    };

    return templates[selectedTemplate] || templates.standard;
  };

  // Standard letter template
  const generateStandardLetter = () => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return `${String(companyInfo.name ?? '').toUpperCase()}
${companyInfo.address}
${companyInfo.registrationNumber}

${currentDate}

${letterData.candidateName}
${letterData.candidateAddress}

Dear ${letterData.candidateName},

JOB OFFER - ${String(letterData.position ?? '').toUpperCase()}

We are delighted to offer you the position of ${letterData.position} with ${companyInfo.name}. Following your successful interview process, we believe you will be an excellent addition to our ${letterData.department} team.

POSITION DETAILS:

Position Title: ${letterData.position}
Department: ${letterData.department}
Reporting to: ${letterData.reportingTo}
Start Date: ${letterData.startDate}
Work Location: ${letterData.location}
Working Arrangement: ${letterData.workType}
Working Hours: ${letterData.workingHours}

COMPENSATION AND BENEFITS:

Annual Salary: ${letterData.salary}
Payment: Monthly in arrears
Probationary Period: ${letterData.probationPeriod}
Notice Period: ${letterData.noticePeriod}

Benefits Package:
${(letterData.benefits || []).map(benefit => `• ${benefit}`).join('\n')}

TERMS AND CONDITIONS:

${letterData.backgroundCheckClause ? '• This offer is subject to satisfactory completion of pre-employment screening, including background checks and reference verification.\n' : ''}${letterData.rightToWorkClause ? '• You must provide evidence of your right to work in the UK before your start date.\n' : ''}${letterData.confidentialityClause ? '• You will be required to sign a confidentiality agreement as part of your employment.\n' : ''}${letterData.nonCompeteClause ? '• A non-compete clause will apply for 12 months following termination of employment.\n' : ''}${letterData.dataProtectionClause ? '• Your personal data will be processed in accordance with our Privacy Policy and GDPR requirements.\n' : ''}

${letterData.customClauses ? `ADDITIONAL TERMS:\n${letterData.customClauses}\n\n` : ''}ACCEPTANCE:

To accept this offer, please sign and return this letter by ${letterData.deadline}. If you have any questions regarding this offer, please contact me directly.

We are excited about the prospect of you joining our team and look forward to your positive response.

Yours sincerely,


${companyInfo.hrDirector}
${companyInfo.hrDirectorTitle}
${companyInfo.name}

Email: ${companyInfo.hrDirectorEmail}
Phone: ${companyInfo.hrDirectorPhone}

---

ACCEPTANCE OF OFFER

I, ${letterData.candidateName}, accept the position of ${letterData.position} with ${companyInfo.name} under the terms and conditions outlined in this letter.

Signature: _________________________    Date: _____________

Print Name: ${letterData.candidateName}`;
  };

  // Executive letter template
  const generateExecutiveLetter = () => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return `${String(companyInfo.name ?? '').toUpperCase()}
${companyInfo.address}
${companyInfo.registrationNumber}

${currentDate}

${letterData.candidateName}
${letterData.candidateAddress}

Dear ${letterData.candidateName},

EXECUTIVE APPOINTMENT - ${String(letterData.position ?? '').toUpperCase()}

On behalf of the Board of Directors, I am pleased to offer you the executive position of ${letterData.position} with ${companyInfo.name}.

Your extensive experience and proven leadership capabilities make you an ideal candidate for this strategic role within our organization.

EXECUTIVE APPOINTMENT DETAILS:

Position: ${letterData.position}
Department: ${letterData.department}
Reporting Structure: Board of Directors
Commencement Date: ${letterData.startDate}
Location: ${letterData.location}
Working Arrangement: ${letterData.workType}

EXECUTIVE COMPENSATION PACKAGE:

Base Salary: ${letterData.salary} per annum
Performance Review: Annual with potential for salary adjustment
Benefits: Comprehensive executive benefits package
Notice Period: ${letterData.noticePeriod}
Probationary Period: ${letterData.probationPeriod}

EXECUTIVE BENEFITS:
${(letterData.benefits || []).map(benefit => `• ${benefit}`).join('\n')}
• Executive health screening
• Company car allowance
• Executive pension scheme
• Professional indemnity insurance

EXECUTIVE RESPONSIBILITIES:

As ${letterData.position}, you will be responsible for strategic leadership, operational excellence, and driving organizational growth in alignment with our corporate objectives.

TERMS OF APPOINTMENT:

${letterData.backgroundCheckClause ? '• Executive background verification and reference checks\n' : ''}${letterData.confidentialityClause ? '• Comprehensive confidentiality and non-disclosure agreements\n' : ''}${letterData.nonCompeteClause ? '• Executive non-compete and non-solicitation clauses\n' : ''}• Fiduciary duties and director responsibilities
• Compliance with corporate governance requirements

${letterData.customClauses ? `SPECIAL TERMS:\n${letterData.customClauses}\n\n` : ''}This appointment is subject to Board approval and execution of the full executive service agreement.

Please confirm your acceptance by ${letterData.deadline}.

We look forward to your leadership and the significant value you will bring to our organization.

Yours sincerely,


${companyInfo.ceo}
${companyInfo.ceoTitle}
${companyInfo.name}

---

ACCEPTANCE OF EXECUTIVE APPOINTMENT

I, ${letterData.candidateName}, accept the executive position of ${letterData.position} with ${companyInfo.name} subject to the terms outlined above and the full executive service agreement.

Executive Signature: _________________________    Date: _____________

Print Name: ${letterData.candidateName}`;
  };

  // Contract letter template
  const generateContractLetter = () => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return `EMPLOYMENT CONTRACT

${String(companyInfo.name ?? '').toUpperCase()}
${companyInfo.address}
${companyInfo.registrationNumber}

CONTRACT OF EMPLOYMENT

This Contract of Employment is made between:

EMPLOYER: ${companyInfo.name}
EMPLOYEE: ${letterData.candidateName}

Date of Contract: ${currentDate}
Commencement Date: ${letterData.startDate}

1. POSITION AND DUTIES

1.1 Job Title: ${letterData.position}
1.2 Department: ${letterData.department}
1.3 Reporting to: ${letterData.reportingTo}
1.4 Location: ${letterData.location}
1.5 Working Pattern: ${letterData.workType}

2. REMUNERATION

2.1 Annual Salary: ${letterData.salary}
2.2 Payment: Monthly in arrears
2.3 Review: Annual salary review

3. WORKING TIME

3.1 Normal Hours: ${letterData.workingHours}
3.2 Overtime: As required by business needs

4. HOLIDAYS

4.1 Annual Leave: As per company policy
4.2 Public Holidays: UK statutory holidays

5. BENEFITS

${(letterData.benefits || []).map((benefit, index) => `5.${index + 1} ${benefit}`).join('\n')}

6. PROBATIONARY PERIOD

6.1 Duration: ${letterData.probationPeriod}
6.2 Review: Performance evaluation at end of probation

7. NOTICE PERIOD

7.1 Employee Notice: ${letterData.noticePeriod}
7.2 Company Notice: ${letterData.noticePeriod}

8. CONFIDENTIALITY

${letterData.confidentialityClause ? '8.1 The Employee agrees to maintain strict confidentiality regarding all company information, trade secrets, and proprietary data.\n8.2 This obligation continues beyond termination of employment.' : '8.1 Standard confidentiality provisions apply.'}

9. POST-EMPLOYMENT RESTRICTIONS

${letterData.nonCompeteClause ? '9.1 Non-compete period: 12 months following termination\n9.2 Non-solicitation of clients and employees' : '9.1 No post-employment restrictions apply.'}

10. DATA PROTECTION

${letterData.dataProtectionClause ? '10.1 Personal data processed in accordance with GDPR\n10.2 Privacy Policy available on company website' : '10.1 Standard data protection provisions apply.'}

${letterData.customClauses ? `11. ADDITIONAL TERMS\n\n${letterData.customClauses}\n\n` : ''}ACCEPTANCE

By signing below, both parties agree to be bound by the terms of this contract.

EMPLOYER:                           EMPLOYEE:

_____________________              _____________________
${companyInfo.hrDirector}                    ${letterData.candidateName}
${companyInfo.hrDirectorTitle}
Date: _______________              Date: _______________

Witness: _____________________     Witness: _____________________`;
  };

  // Intern letter template
  const generateInternLetter = () => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return `${String(companyInfo.name ?? '').toUpperCase()}
${companyInfo.address}
${companyInfo.registrationNumber}

${currentDate}

${letterData.candidateName}
${letterData.candidateAddress}

Dear ${letterData.candidateName},

INTERNSHIP OFFER - ${letterData.position}

We are pleased to offer you an internship position as ${letterData.position} with ${companyInfo.name}. This internship will provide you with valuable professional experience and learning opportunities.

INTERNSHIP DETAILS:

Position: ${letterData.position}
Department: ${letterData.department}
Supervisor: ${letterData.reportingTo}
Start Date: ${letterData.startDate}
Duration: ${letterData.probationPeriod}
Location: ${letterData.location}
Working Hours: ${letterData.workingHours}

COMPENSATION:

${letterData.salary.includes('£') ? `Stipend: ${letterData.salary}` : `This is an unpaid internship position`}
Expenses: Reasonable travel expenses reimbursed

LEARNING OBJECTIVES:

• Gain practical experience in ${letterData.department}
• Develop professional skills and competencies
• Contribute to meaningful projects and initiatives
• Receive mentorship and career guidance

INTERNSHIP BENEFITS:

${(letterData.benefits || []).map(benefit => `• ${benefit}`).join('\n')}
• Professional development workshops
• Networking opportunities
• Certificate of completion
• Potential for future employment consideration

TERMS AND CONDITIONS:

• This is a temporary internship position
• Performance evaluation at mid-point and completion
• Confidentiality agreement required
• Compliance with company policies and procedures

${letterData.customClauses ? `ADDITIONAL INFORMATION:\n${letterData.customClauses}\n\n` : ''}To accept this internship offer, please sign and return this letter by ${letterData.deadline}.

We look forward to welcoming you to our team and supporting your professional development.

Yours sincerely,


${companyInfo.hrDirector}
${companyInfo.hrDirectorTitle}
${companyInfo.name}

---

ACCEPTANCE OF INTERNSHIP OFFER

I, ${letterData.candidateName}, accept the internship position of ${letterData.position} with ${companyInfo.name} under the terms outlined above.

Signature: _________________________    Date: _____________

Print Name: ${letterData.candidateName}`;
  };

  // Consultant letter template
  const generateConsultantLetter = () => {
    const currentDate = new Date().toLocaleDateString('en-GB', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    });

    return `CONSULTANCY AGREEMENT

${String(companyInfo.name ?? '').toUpperCase()}
${companyInfo.address}
${companyInfo.registrationNumber}

${currentDate}

${letterData.candidateName}
${letterData.candidateAddress}

Dear ${letterData.candidateName},

CONSULTANCY ENGAGEMENT - ${letterData.position}

We are pleased to engage your services as an independent consultant for the role of ${letterData.position}.

ENGAGEMENT DETAILS:

Consultant Role: ${letterData.position}
Project/Department: ${letterData.department}
Project Manager: ${letterData.reportingTo}
Commencement: ${letterData.startDate}
Initial Term: ${letterData.probationPeriod}
Location: ${letterData.location}
Working Arrangement: ${letterData.workType}

COMMERCIAL TERMS:

Consultant Fee: ${letterData.salary}
Payment Terms: Monthly in arrears
Expenses: Pre-approved expenses reimbursed
Invoice Requirements: Monthly invoicing required

SCOPE OF SERVICES:

• Provide expert consultancy in ${letterData.department}
• Deliver agreed project milestones
• Attend meetings and provide regular updates
• Maintain professional standards and quality

CONSULTANT OBLIGATIONS:

• Professional indemnity insurance required
• Compliance with IR35 regulations
• Confidentiality and data protection
• Health and safety compliance

TERMS OF ENGAGEMENT:

• Independent contractor relationship
• No employment rights or benefits
• Termination: ${letterData.noticePeriod} notice
• Intellectual property assignment

${letterData.confidentialityClause ? 'CONFIDENTIALITY:\n• Strict confidentiality regarding all project information\n• Non-disclosure of proprietary data and processes\n\n' : ''}${letterData.customClauses ? `SPECIAL TERMS:\n${letterData.customClauses}\n\n` : ''}This engagement is subject to execution of the full consultancy agreement and compliance with all legal requirements.

Please confirm your acceptance by ${letterData.deadline}.

We look forward to a successful consultancy engagement.

Yours sincerely,


${companyInfo.hrDirector}
${companyInfo.hrDirectorTitle}
${companyInfo.name}

---

ACCEPTANCE OF CONSULTANCY ENGAGEMENT

I, ${letterData.candidateName}, accept the consultancy engagement for ${letterData.position} with ${companyInfo.name} subject to the terms outlined above.

Consultant Signature: _________________________    Date: _____________

Print Name: ${letterData.candidateName}`;
  };

  // Generate PDF (simulated)
  const generatePDF = async () => {
    setIsGenerating(true);
    
    try {
      // Simulate PDF generation
      await new Promise(resolve => setTimeout(resolve, 3000));
      
      const letterContent = generateLetterContent();
      const fileName = `JobOffer_${letterData.candidateName.replace(/\s+/g, '_')}_${Date.now()}.pdf`;
      
      // In a real implementation, you would use a PDF generation library
      // For now, we'll create a text file as a placeholder
      const fileUri = FileSystem.documentDirectory + fileName.replace('.pdf', '.txt');
      
      await FileSystem.writeAsStringAsync(fileUri, letterContent);
      
      // Save to history
      await saveToHistory({
        ...letterData,
        fileName,
        fileUri,
      });

      // Call callback if provided
      if (onDownload) {
        onDownload({ fileName, fileUri, content: letterContent });
      }

      Alert.alert(
        'PDF Generated Successfully!',
        `Job offer letter has been generated for ${letterData.candidateName}`,
        [
          {
            text: 'Share',
            onPress: () => shareDocument(fileUri),
          },
          {
            text: 'OK',
            style: 'default',
          },
        ]
      );

    } catch (error) {
      Alert.alert('Error', 'Failed to generate PDF. Please try again.');
    } finally {
      setIsGenerating(false);
    }
  };

  // Share document
  const shareDocument = async (fileUri) => {
    try {
      if (await Sharing.isAvailableAsync()) {
        await Sharing.shareAsync(fileUri);
      } else {
        await Share.share({
          message: generateLetterContent(),
          title: `Job Offer Letter - ${letterData.candidateName}`,
        });
      }
    } catch (error) {
      Alert.alert('Error', 'Failed to share document');
    }
  };

  // Save as custom template
  const saveAsTemplate = async () => {
    Alert.prompt(
      'Save Template',
      'Enter a name for this template:',
      [
        { text: 'Cancel', style: 'cancel' },
        {
          text: 'Save',
          onPress: async (templateName) => {
            if (templateName) {
              try {
                const newTemplate = {
                  id: Date.now().toString(),
                  name: templateName,
                  content: generateLetterContent(),
                  template: selectedTemplate,
                  data: letterData,
                  createdAt: new Date().toISOString(),
                };

                const updatedTemplates = [...customTemplates, newTemplate];
                setCustomTemplates(updatedTemplates);
                try { await apiRequest('offer-letter-templates', { method: 'PUT', body: { templates: updatedTemplates } }); } catch(e) { console.warn('API save failed:', e.message); }
                await AsyncStorage.setItem('customOfferLetterTemplates', JSON.stringify(updatedTemplates));
                
                Alert.alert('Success', 'Template saved successfully!');
              } catch (error) {
                Alert.alert('Error', 'Failed to save template');
              }
            }
          },
        },
      ],
      'plain-text'
    );
  };

  // Render template selector
  const renderTemplateSelector = () => {
    const templates = [
      { id: 'standard', name: 'Standard', icon: 'file-document', color: '#4A90E2', description: 'Standard employment offer letter' },
      { id: 'executive', name: 'Executive', icon: 'crown', color: '#EF4444', description: 'Executive-level appointment letter' },
      { id: 'contract', name: 'Contract', icon: 'file-contract', color: '#8B5CF6', description: 'Formal employment contract' },
      { id: 'intern', name: 'Internship', icon: 'school', color: '#10B981', description: 'Internship offer letter' },
      { id: 'consultant', name: 'Consultant', icon: 'briefcase-account', color: '#F59E0B', description: 'Consultancy engagement letter' },
    ];

    return (
      <Modal visible={showTemplateSelector} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select Letter Template</Text>
              <TouchableOpacity onPress={() => setShowTemplateSelector(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalContent}>
              {(templates || []).map((template) => (
                <TouchableOpacity
                  key={template.id}
                  style={[
                    styles.templateOption,
                    selectedTemplate === template.id && styles.selectedTemplateOption,
                  ]}
                  onPress={() => {
                    setSelectedTemplate(template.id);
                    setShowTemplateSelector(false);
                  }}
                >
                  <View style={[styles.templateIcon, { backgroundColor: template.color }]}>
                    <MaterialCommunityIcons name={template.icon} size={24} color="#ffffff" />
                  </View>
                  <View style={styles.templateInfo}>
                    <Text style={[
                      styles.templateName,
                      selectedTemplate === template.id && styles.selectedTemplateName,
                    ]}>
                      {template.name}
                    </Text>
                    <Text style={styles.templateDescription}>
                      {template.description}
                    </Text>
                  </View>
                  {selectedTemplate === template.id && (
                    <MaterialCommunityIcons name="check-circle" size={24} color="#10B981" />
                  )}
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // Render letter settings modal
  const renderLetterSettings = () => {
    return (
      <Modal visible={showSettings} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.modalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Letter Settings</Text>
              <TouchableOpacity onPress={() => setShowSettings(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.modalContent}>
              <View style={styles.settingsGroup}>
                <Text style={styles.settingsLabel}>Document Options</Text>
                
                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Include Company Logo</Text>
                    <Text style={styles.switchDescription}>Add company logo to header</Text>
                  </View>
                  <Switch
                    value={letterSettings.includeCompanyLogo}
                    onValueChange={(value) => handleLetterSettingsChange('includeCompanyLogo', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.includeCompanyLogo ? '#ffffff' : '#f4f3f4'}
                  />
                </View>

                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Include Watermark</Text>
                    <Text style={styles.switchDescription}>Add confidential watermark</Text>
                  </View>
                  <Switch
                    value={letterSettings.includeWatermark}
                    onValueChange={(value) => handleLetterSettingsChange('includeWatermark', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.includeWatermark ? '#ffffff' : '#f4f3f4'}
                  />
                </View>

                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Page Numbers</Text>
                    <Text style={styles.switchDescription}>Include page numbering</Text>
                  </View>
                  <Switch
                    value={letterSettings.pageNumbers}
                    onValueChange={(value) => handleLetterSettingsChange('pageNumbers', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.pageNumbers ? '#ffffff' : '#f4f3f4'}
                  />
                </View>

                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Digital Signature</Text>
                    <Text style={styles.switchDescription}>Enable digital signature fields</Text>
                  </View>
                  <Switch
                    value={letterSettings.digitalSignature}
                    onValueChange={(value) => handleLetterSettingsChange('digitalSignature', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.digitalSignature ? '#ffffff' : '#f4f3f4'}
                  />
                </View>

                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Legal Review Required</Text>
                    <Text style={styles.switchDescription}>Mark for legal team review</Text>
                  </View>
                  <Switch
                    value={letterSettings.legalReview}
                    onValueChange={(value) => handleLetterSettingsChange('legalReview', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.legalReview ? '#ffffff' : '#f4f3f4'}
                  />
                </View>

                <View style={styles.switchSetting}>
                  <View style={styles.switchInfo}>
                    <Text style={styles.switchLabel}>Urgent Offer</Text>
                    <Text style={styles.switchDescription}>Mark as urgent/time-sensitive</Text>
                  </View>
                  <Switch
                    value={letterSettings.urgentOffer}
                    onValueChange={(value) => handleLetterSettingsChange('urgentOffer', value)}
                    trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
                    thumbColor={letterSettings.urgentOffer ? '#ffffff' : '#f4f3f4'}
                  />
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </Modal>
    );
  };

  // Render preview modal
  const renderPreview = () => {
    return (
      <Modal visible={showPreview} transparent animationType="slide">
        <View style={styles.modalOverlay}>
          <View style={styles.previewModalContainer}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Letter Preview</Text>
              <TouchableOpacity onPress={() => setShowPreview(false)}>
                <AntDesign name="close" size={24} color="#64748B" />
              </TouchableOpacity>
            </View>
            
            <ScrollView style={styles.previewContent}>
              <View style={styles.letterPreview}>
                <Text style={styles.letterContent}>{generateLetterContent()}</Text>
              </View>
            </ScrollView>
            
            <View style={styles.previewActions}>
              <TouchableOpacity
                style={styles.previewActionButton}
                onPress={saveAsTemplate}
              >
                <MaterialCommunityIcons name="content-save" size={20} color="#4A90E2" />
                <Text style={styles.previewActionText}>Save Template</Text>
              </TouchableOpacity>
              
              <TouchableOpacity
                style={[styles.previewActionButton, styles.generateButton]}
                onPress={() => {
                  setShowPreview(false);
                  generatePDF();
                }}
                disabled={isGenerating}
              >
                <MaterialCommunityIcons name="file-pdf-box" size={20} color="#ffffff" />
                <Text style={[styles.previewActionText, styles.generateButtonText]}>Generate PDF</Text>
              </TouchableOpacity>
            </View>
          </View>
        </View>
      </Modal>
    );
  };

  return (
    <KeyboardAvoidingView
      style={styles.container}
      behavior={Platform.OS === 'ios' ? 'padding' : 'height'}
    >
      <StatusBar barStyle="light-content" backgroundColor="#4A90E2" />
      
      {/* Header */}
      <LinearGradient colors={['#4A90E2', '#357ABD']} style={styles.header}>
        <View style={styles.headerContent}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => {
              if (onClose) onClose();
              else if (navigation) navigation.goBack();
            }}
          >
            <Ionicons name="arrow-back" size={24} color="white" />
          </TouchableOpacity>
          <Text style={styles.headerTitle}>Job Offer Letter</Text>
          <TouchableOpacity
            style={styles.settingsButton}
            onPress={() => setShowSettings(true)}
          >
            <MaterialCommunityIcons name="cog" size={24} color="white" />
          </TouchableOpacity>
        </View>
      </LinearGradient>

      <ScrollView style={styles.content} showsVerticalScrollIndicator={false}>
        {/* Candidate Info Card */}
        <View style={styles.candidateCard}>
          <View style={styles.candidateHeader}>
            <View style={styles.candidateAvatar}>
              <Text style={styles.candidateInitials}>
                {letterData.candidateName.split(' ').map(n => n[0]).join('').toUpperCase()}
              </Text>
            </View>
            <View style={styles.candidateInfo}>
              <Text style={styles.candidateName}>{letterData.candidateName}</Text>
              <Text style={styles.candidatePosition}>{letterData.position}</Text>
              <Text style={styles.candidateEmail}>{letterData.candidateEmail}</Text>
            </View>
            <View style={styles.letterBadge}>
              <Text style={styles.letterBadgeText}>LETTER</Text>
            </View>
          </View>
        </View>

        {/* Template Selection */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Letter Template</Text>
          <TouchableOpacity
            style={styles.templateSelector}
            onPress={() => setShowTemplateSelector(true)}
          >
            <MaterialCommunityIcons name="file-document" size={20} color="#4A90E2" />
            <Text style={styles.templateSelectorText}>
              {selectedTemplate.charAt(0).toUpperCase() + selectedTemplate.slice(1)} Template
            </Text>
            <MaterialCommunityIcons name="chevron-down" size={20} color="#64748B" />
          </TouchableOpacity>
        </View>

        {/* Basic Information */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Basic Information</Text>
          
          <View style={styles.formGroup}>
            <Text style={styles.label}>Candidate Name</Text>
            <TextInput
              style={styles.input}
              value={letterData.candidateName}
              onChangeText={(text) => handleInputChange('candidateName', text)}
              placeholder="Enter candidate name"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Candidate Address</Text>
            <TextInput
              style={styles.textArea}
              value={letterData.candidateAddress}
              onChangeText={(text) => handleInputChange('candidateAddress', text)}
              placeholder="Enter candidate address"
              multiline
              numberOfLines={3}
              textAlignVertical="top"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Position</Text>
            <TextInput
              style={styles.input}
              value={letterData.position}
              onChangeText={(text) => handleInputChange('position', text)}
              placeholder="Enter position title"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Department</Text>
            <TextInput
              style={styles.input}
              value={letterData.department}
              onChangeText={(text) => handleInputChange('department', text)}
              placeholder="Enter department"
            />
          </View>
        </View>

        {/* Employment Details */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Employment Details</Text>
          
          <View style={styles.formGroup}>
            <Text style={styles.label}>Salary</Text>
            <TextInput
              style={styles.input}
              value={letterData.salary}
              onChangeText={(text) => handleInputChange('salary', text)}
              placeholder="Enter salary amount"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Start Date</Text>
            <TextInput
              style={styles.input}
              value={letterData.startDate}
              onChangeText={(text) => handleInputChange('startDate', text)}
              placeholder="Enter start date"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Offer Deadline</Text>
            <TextInput
              style={styles.input}
              value={letterData.deadline}
              onChangeText={(text) => handleInputChange('deadline', text)}
              placeholder="Enter offer deadline"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Reporting To</Text>
            <TextInput
              style={styles.input}
              value={letterData.reportingTo}
              onChangeText={(text) => handleInputChange('reportingTo', text)}
              placeholder="Enter reporting manager"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Working Hours</Text>
            <TextInput
              style={styles.input}
              value={letterData.workingHours}
              onChangeText={(text) => handleInputChange('workingHours', text)}
              placeholder="Enter working hours"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Probation Period</Text>
            <TextInput
              style={styles.input}
              value={letterData.probationPeriod}
              onChangeText={(text) => handleInputChange('probationPeriod', text)}
              placeholder="Enter probation period"
            />
          </View>

          <View style={styles.formGroup}>
            <Text style={styles.label}>Notice Period</Text>
            <TextInput
              style={styles.input}
              value={letterData.noticePeriod}
              onChangeText={(text) => handleInputChange('noticePeriod', text)}
              placeholder="Enter notice period"
            />
          </View>
        </View>

        {/* Benefits */}
        <View style={styles.section}>
          <View style={styles.sectionHeader}>
            <Text style={styles.sectionTitle}>Benefits Package</Text>
            <TouchableOpacity style={styles.addButton} onPress={addBenefit}>
              <MaterialCommunityIcons name="plus" size={20} color="#4A90E2" />
            </TouchableOpacity>
          </View>
          
          {(letterData.benefits || []).map((benefit, index) => (
            <View key={index} style={styles.benefitRow}>
              <TextInput
                style={[styles.input, styles.benefitInput]}
                value={benefit}
                onChangeText={(text) => handleBenefitChange(index, text)}
                placeholder="Enter benefit"
              />
              <TouchableOpacity
                style={styles.removeButton}
                onPress={() => removeBenefit(index)}
              >
                <MaterialCommunityIcons name="close" size={20} color="#EF4444" />
              </TouchableOpacity>
            </View>
          ))}
        </View>

        {/* Legal Clauses */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Legal Clauses</Text>
          
          <View style={styles.switchContainer}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>Confidentiality Clause</Text>
              <Text style={styles.switchDescription}>Include confidentiality agreement</Text>
            </View>
            <Switch
              value={letterData.confidentialityClause}
              onValueChange={(value) => handleInputChange('confidentialityClause', value)}
              trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
              thumbColor={letterData.confidentialityClause ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.switchContainer}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>Non-Compete Clause</Text>
              <Text style={styles.switchDescription}>Include non-compete restrictions</Text>
            </View>
            <Switch
              value={letterData.nonCompeteClause}
              onValueChange={(value) => handleInputChange('nonCompeteClause', value)}
              trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
              thumbColor={letterData.nonCompeteClause ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.switchContainer}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>Background Check Clause</Text>
              <Text style={styles.switchDescription}>Include background check requirement</Text>
            </View>
            <Switch
              value={letterData.backgroundCheckClause}
              onValueChange={(value) => handleInputChange('backgroundCheckClause', value)}
              trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
              thumbColor={letterData.backgroundCheckClause ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.switchContainer}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>Right to Work Clause</Text>
              <Text style={styles.switchDescription}>Include right to work verification</Text>
            </View>
            <Switch
              value={letterData.rightToWorkClause}
              onValueChange={(value) => handleInputChange('rightToWorkClause', value)}
              trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
              thumbColor={letterData.rightToWorkClause ? '#ffffff' : '#f4f3f4'}
            />
          </View>

          <View style={styles.switchContainer}>
            <View style={styles.switchInfo}>
              <Text style={styles.switchLabel}>Data Protection Clause</Text>
              <Text style={styles.switchDescription}>Include GDPR compliance clause</Text>
            </View>
            <Switch
              value={letterData.dataProtectionClause}
              onValueChange={(value) => handleInputChange('dataProtectionClause', value)}
              trackColor={{ false: '#CBD5E1', true: '#4A90E2' }}
              thumbColor={letterData.dataProtectionClause ? '#ffffff' : '#f4f3f4'}
            />
          </View>
        </View>

        {/* Custom Clauses */}
        <View style={styles.section}>
          <Text style={styles.sectionTitle}>Custom Clauses</Text>
          <TextInput
            style={styles.textArea}
            value={letterData.customClauses}
            onChangeText={(text) => handleInputChange('customClauses', text)}
            placeholder="Add any custom clauses or additional terms..."
            multiline
            numberOfLines={6}
            textAlignVertical="top"
          />
        </View>
      </ScrollView>

      {/* Action Buttons */}
      <View style={styles.actionContainer}>
        <TouchableOpacity
          style={styles.previewButton}
          onPress={() => setShowPreview(true)}
        >
          <MaterialCommunityIcons name="eye" size={20} color="#4A90E2" />
          <Text style={styles.previewButtonText}>Preview</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={styles.saveTemplateButton}
          onPress={saveAsTemplate}
        >
          <MaterialCommunityIcons name="content-save" size={20} color="#10B981" />
          <Text style={styles.saveTemplateButtonText}>Save</Text>
        </TouchableOpacity>

        <TouchableOpacity
          style={[styles.generatePDFButton, isGenerating && styles.generatePDFButtonDisabled]}
          onPress={generatePDF}
          disabled={isGenerating}
        >
          {isGenerating ? (
            <ActivityIndicator size={20} color="#ffffff" />
          ) : (
            <>
              <MaterialCommunityIcons name="file-pdf-box" size={20} color="#ffffff" />
              <Text style={styles.generatePDFButtonText}>Generate PDF</Text>
            </>
          )}
        </TouchableOpacity>
      </View>

      {/* Modals */}
      {renderTemplateSelector()}
      {renderLetterSettings()}
      {renderPreview()}
    </KeyboardAvoidingView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f8f9fa',
  },
  header: {
    paddingTop: 50,
    paddingBottom: 20,
    paddingHorizontal: 20,
  },
  headerContent: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  backButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: 'white',
    flex: 1,
    textAlign: 'center',
  },
  settingsButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: 'rgba(255, 255, 255, 0.2)',
  },
  content: {
    flex: 1,
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    marginTop: -10,
    paddingHorizontal: 20,
    paddingTop: 20,
  },
  candidateCard: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 20,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  candidateHeader: {
    flexDirection: 'row',
    alignItems: 'center',
  },
  candidateAvatar: {
    width: 56,
    height: 56,
    borderRadius: 28,
    backgroundColor: '#4A90E2',
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  candidateInitials: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#ffffff',
  },
  candidateInfo: {
    flex: 1,
  },
  candidateName: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 2,
  },
  candidatePosition: {
    fontSize: 14,
    color: '#4A90E2',
    marginBottom: 2,
  },
  candidateEmail: {
    fontSize: 12,
    color: '#64748B',
  },
  letterBadge: {
    backgroundColor: '#8B5CF6',
    paddingHorizontal: 8,
    paddingVertical: 4,
    borderRadius: 4,
  },
  letterBadgeText: {
    fontSize: 10,
    color: '#ffffff',
    fontWeight: 'bold',
  },
  section: {
    marginBottom: 24,
  },
  sectionHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: 12,
  },
  sectionTitle: {
    fontSize: 18,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  addButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#f0f8ff',
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  templateSelector: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    gap: 12,
  },
  templateSelectorText: {
    flex: 1,
    fontSize: 16,
    color: '#0f172a',
    fontWeight: '600',
  },
  formGroup: {
    marginBottom: 16,
  },
  label: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 8,
  },
  input: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    fontSize: 16,
    color: '#0f172a',
  },
  textArea: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    paddingHorizontal: 16,
    paddingVertical: 14,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    fontSize: 16,
    color: '#0f172a',
    minHeight: 100,
    textAlignVertical: 'top',
  },
  benefitRow: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
    gap: 8,
  },
  benefitInput: {
    flex: 1,
    marginBottom: 0,
  },
  removeButton: {
    padding: 8,
    borderRadius: 20,
    backgroundColor: '#fef2f2',
    borderWidth: 1,
    borderColor: '#fecaca',
  },
  switchContainer: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginBottom: 8,
  },
  switchInfo: {
    flex: 1,
    marginRight: 16,
  },
  switchLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 4,
  },
  switchDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  actionContainer: {
    flexDirection: 'row',
    padding: 20,
    paddingTop: 16,
    backgroundColor: '#ffffff',
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: 8,
  },
  previewButton: {
    flex: 1,
    backgroundColor: '#f0f8ff',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  previewButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
  },
  saveTemplateButton: {
    flex: 1,
    backgroundColor: '#f0fdf4',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#10B981',
  },
  saveTemplateButtonText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#10B981',
  },
  generatePDFButton: {
    flex: 2,
    backgroundColor: '#8B5CF6',
    paddingVertical: 14,
    borderRadius: 12,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
  },
  generatePDFButtonDisabled: {
    backgroundColor: '#94a3b8',
  },
  generatePDFButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#ffffff',
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContainer: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.8,
  },
  previewModalContainer: {
    backgroundColor: '#ffffff',
    borderTopLeftRadius: 20,
    borderTopRightRadius: 20,
    maxHeight: height * 0.9,
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: 20,
    borderBottomWidth: 1,
    borderBottomColor: '#e2e8f0',
  },
  modalTitle: {
    fontSize: 20,
    fontWeight: 'bold',
    color: '#0f172a',
  },
  modalContent: {
    flex: 1,
    padding: 20,
  },
  templateOption: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    marginBottom: 12,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  selectedTemplateOption: {
    borderColor: '#10B981',
    backgroundColor: '#f0fdf4',
  },
  templateIcon: {
    width: 48,
    height: 48,
    borderRadius: 24,
    justifyContent: 'center',
    alignItems: 'center',
    marginRight: 16,
  },
  templateInfo: {
    flex: 1,
  },
  templateName: {
    fontSize: 16,
    fontWeight: 'bold',
    color: '#0f172a',
    marginBottom: 4,
  },
  selectedTemplateName: {
    color: '#10B981',
  },
  templateDescription: {
    fontSize: 14,
    color: '#64748B',
  },
  settingsGroup: {
    marginBottom: 24,
  },
  settingsLabel: {
    fontSize: 16,
    fontWeight: '600',
    color: '#0f172a',
    marginBottom: 12,
  },
  switchSetting: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
    marginBottom: 8,
  },
  previewContent: {
    flex: 1,
    padding: 20,
  },
  letterPreview: {
    backgroundColor: '#f8fafc',
    borderRadius: 12,
    padding: 16,
    borderWidth: 1,
    borderColor: '#e2e8f0',
  },
  letterContent: {
    fontSize: 12,
    color: '#0f172a',
    lineHeight: 18,
    fontFamily: Platform.OS === 'ios' ? 'Courier' : 'monospace',
  },
  previewActions: {
    flexDirection: 'row',
    padding: 20,
    borderTopWidth: 1,
    borderTopColor: '#e2e8f0',
    gap: 8,
  },
  previewActionButton: {
    flex: 1,
    backgroundColor: '#f0f8ff',
    paddingVertical: 12,
    borderRadius: 8,
    alignItems: 'center',
    flexDirection: 'row',
    justifyContent: 'center',
    gap: 6,
    borderWidth: 1,
    borderColor: '#4A90E2',
  },
  previewActionText: {
    fontSize: 14,
    fontWeight: '600',
    color: '#4A90E2',
  },
  generateButton: {
    backgroundColor: '#8B5CF6',
    borderColor: '#8B5CF6',
  },
  generateButtonText: {
    color: '#ffffff',
  },
});

export default JobOfferLetterTemplate_Production;